import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {Kpi} from '../../../../model/kpi';
import {GenericGraphComponent} from '../../../shared/generic-graph/generic-graph.component';
import {translate, TranslocoService} from '@ngneat/transloco';
import {KpisService} from '../../../../services/kpis.service';
import {MeasuresService} from '../../../../services/measures.service';
import {TimezoneService} from '../../../../services/timezone.service';
import {ConversorsService} from "../../../../services/conversors.service";
import {
  KpiRecalculateMeasuresDialogComponent
} from "./kpi-recalculate-measures-dialog/kpi-recalculate-measures-dialog.component";
import {DialogService} from "primeng/dynamicdialog";
import {MessageService} from "primeng/api";

type ModeOption = { label: string, value: 'chart' | 'table' };
type MeasurementUnit = { origin: string, target: string, name: string };
type SelectMeasurementUnit = { origin: string, target: any, name: string };
type SelectItemGr = { measure: string, label: string, value: string, items: MeasurementUnit[] };

@Component({
  selector: 'app-kpi-measures',
  templateUrl: './kpi-measures.component.html',
  styleUrls: ['./kpi-measures.component.scss']
})
export class KpiMeasuresComponent implements OnInit {

  @Input()
  public kpi: Kpi;

  @ViewChild('measuresChart')
  public measuresChart: GenericGraphComponent;

  public rangeDates: Date[];
  public tableMeasures: any;
  public graphMeasures: any;
  public graphAxis: any[];

  public loadingMeasures = false;
  public modeOptions: ModeOption[];
  public selectedModeOption: ModeOption;

  public convert = this.conversorsService.convert;

  public actionsMeasure: any[];
  public displayActionsDialog: boolean;
  public measurementUnit: SelectMeasurementUnit;
  public groupedMeasurementUnit: SelectItemGr[];

  public totalMeasures: number;
  public rows = 10;
  public first = 0;
  public exportVisible: boolean;

  public exportChart;

  constructor(private kpisService: KpisService,
              private translocoService: TranslocoService,
              private timezoneService: TimezoneService,
              private measuresService: MeasuresService,
              private dialogService: DialogService,
              private messageService: MessageService,
              private conversorsService: ConversorsService) {
    this.displayActionsDialog = false;
    const lastWeek = new Date();
    lastWeek.setHours(lastWeek.getHours() - 24 * 7);
    this.rangeDates = [lastWeek, new Date()];
    this.buildModeOptions();
    this.actionsMeasure = [];
  }

  ngOnInit(): void {
    this.translocoService.selectTranslate('common.new').subscribe(() => {
      this.buildModeOptions();
      this.searchMeasures();
      this.loadMeasurementUnit();
      this.actionsMeasure = [
        {label: translate('model.conversion'), icon: 'pi pi-cog', command: () => this.showConversions()},
        {label: translate('common.exportXls'), icon: 'pi pi-file-excel', command:() => this.showExportToXls()},
        {label: translate('common.downloadPng'), icon: 'pi pi-download', command: () => this.downloadChart()},
        {label: translate('misc.recalculate'), icon: 'pi pi-history', command: () => this.showRecalculateMeasures()},
      ];
    });
  }

  showConversions(): void {
    this.displayActionsDialog = true;
    this.measuresChart.resize();
  }

  hideConversions(): void {
    this.displayActionsDialog = false;
    this.measuresChart.resize();
  }

  showExportToXls(): void {
    this.exportVisible = true;
    this.selectedModeOption = this.modeOptions[1];
  }

  private showRecalculateMeasures() {
    const ref = this.dialogService.open(KpiRecalculateMeasuresDialogComponent, {
      data: {timePeriod: this.kpi.timePeriod, kpiTimezone: this.kpi.timezone},
      header: translate('misc.recalculateInRange')
    });
    ref.onClose.subscribe((datesRange?: number[]) => {
      if (datesRange) {
        this.kpisService.createPendingKpiCalculations(this.kpi.uuid, datesRange[0], datesRange[1])
          .subscribe(() => {
            this.messageService.add({
              severity: 'success',
              summary: translate('success.generic.recalculating'),
              detail: translate('success.kpis.recalculatingAndDuration')
            });
          });
      }
    });
  }

  downloadChart(): void {
    if (this.selectedModeOption.value === 'table') {
      this.selectedModeOption = this.modeOptions[0];
    }
    try {
      setTimeout(() => {
        if (this.measuresChart.chart.axes != undefined) {
          this.exportChart = this.measuresChart.chart;
        }
        this.measuresChart.download(this.exportChart);
      });
    } catch (ignored) {}
  }

  private buildModeOptions(): void {
    this.modeOptions = [
      {label: translate('misc.mode.chart'), value: 'chart'},
      {label: translate('misc.mode.table'), value: 'table'}
    ];
    this.selectedModeOption = this.modeOptions[0];
  }

  searchMeasures(): void {

    this.loadingMeasures = true;
    const utcRange = this.timezoneService.timestampRangeFromCalendarData(this.rangeDates);
    this.totalMeasures = 0;
    const totalGraphMeasures = [];
    const totalTableMeasures = [];
    const suffixes = [];
    const promises = [];
    let unitTarget = '';
    let unitOrigin = '';
    let unit = null;

    if (this.kpi.measurementType && this.kpi.measurementType.unit) {
      if (this.measurementUnit != undefined) {
        const target = this.measurementUnit.target;
        unit = {
          target: target,
          origin: this.measurementUnit.origin,
          name: `${target} (${this.convert().describe(target).plural})`
        };
      }

      unitTarget = (unit && unit.origin == this.kpi.measurementType.unit ? unit.target : this.kpi.measurementType.unit ?? '')
      unitOrigin = this.kpi.measurementType.unit;
    }

    const suffix = ' ' + unitTarget;
    let axisIndex;
    if (!suffixes.includes(suffix)) {
      suffixes.push(suffix);
    }
    axisIndex = suffixes.indexOf(suffix);

  // TODO: Quitar el sort de measures cuando el ticket 1380 este solucionado.
    const promise = this.measuresService.searchKpiMeasures (
          this.kpi.tenant.uuid, this.kpi.uuid, utcRange.from, utcRange.until)
          .toPromise().then(response => {
            if (response.measures && response.measures.length >= 0) {
                let data;
                if (this.kpi.measurementType && this.kpi.measurementType.unit) {
                  data = this.measuresService.buildSensorMeasures(response.measures.sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1)), unitOrigin, unitTarget);
                } else {
                  data = this.measuresService.buildSensorMeasures(response.measures.sort((a, b) => (a.timestamp < b.timestamp ? -1 : 1)));
                }
                totalGraphMeasures.push({
                  name: this.kpi.name,
                  data: data,
                  yAxis: axisIndex,
                  tooltip: {valueSuffix: suffix}
                });
                response.measures.forEach(measure => {
                  measure.measurementName = this.kpi.name;
                  measure.unit = unitTarget;
                  if (this.kpi.measurementType && this.kpi.measurementType.unit) {
                    measure.value = unit && measure.unit != this.kpi.measurementType.unit ? this.measuresService.convertValue(parseFloat(measure.value), unitOrigin, unitTarget) : measure.value;
                  }
                  totalTableMeasures.push(measure);
                });
            }
          });
    promises.push(promise);

    Promise.all(promises).then(() => {
      this.graphMeasures = undefined;
      this.tableMeasures = undefined;
      this.totalMeasures = totalTableMeasures.length;
      this.graphAxis = suffixes.map(suffix => ( { labels: { format: '{value}' + suffix }, title: { enabled: false } } ) );
      setTimeout(() => this.graphMeasures = (totalGraphMeasures.length > 0 ) ? totalGraphMeasures : undefined, 0);
      setTimeout(() => this.tableMeasures = (totalTableMeasures.length > 0 ) ? totalTableMeasures : undefined, 0);
      // this.buildAndConfigureMap(this.mapMeasures);
    }).finally(() => this.loadingMeasures = false);
  }

  private loadMeasurementUnit(): void {
    const units = this.convert().possibilities();
    this.groupedMeasurementUnit = [];

    if (this.kpi.measurementType && this.kpi.measurementType.unit) {
      const originUnit = units.find(measure => measure === this.kpi.measurementType.unit);
      const items: MeasurementUnit[] = [];

      this.convert().from(originUnit).possibilities().forEach(poss => {
        items.push({
          origin: originUnit,
          target: poss,
          name: `${poss} (${this.convert().describe(poss).plural})`
        })
      });
      this.groupedMeasurementUnit.push({
        measure: this.kpi.name,
        label: originUnit,
        value: originUnit,
        items: items
      });
      this.measurementUnit = {
        origin: originUnit,
        target: originUnit,
        name: `${originUnit} (${this.convert().describe(originUnit).plural})`
      };
    }
  }

  clearConversions(): void {
    this.measurementUnit = null;
    this.searchMeasures();
    this.loadMeasurementUnit();
    this.displayActionsDialog = false;
  }

}
